import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { StyledModalBody } from "../../../../../components/Modals";
import { ButtonBodyVariant } from "../../../../../components/ui/ButtonVariant";
import { useTranslation } from "react-i18next";
import { closeActiveModal, openModal } from "../../../../../helper/modals";
import { Flex } from "../../../../../components/ui/Flex";
import { IoWarningOutline } from "react-icons/io5";
import { Palette } from "../../../../../components/utils";
import { DescriptionText, IconBackground, LoadingSpinner, ModalButtonsContainer, PaymentModal, TitleText } from "../../../PaymentComponents";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../../../components/ui/Form";
import { useForm } from "react-hook-form";
import * as Yup from 'yup';
import { yupResolver } from "@hookform/resolvers/yup";
import { SettingsModalInput } from "../../../../settings/SettingsModals";
import { addPaymentMethod, createResidentVopayAccount, syncPaymentMethod } from "../../../../../redux/actions/payments";
import { PAYMENT_METHOD_TYPE_BANK } from "../../../../../redux/constants";
import { setToast } from "../../../../../redux/actions/window";
import { extractVopayTokenFromHref } from "../../../../../helper/vopayRedirect";
import {
  dispatchPaymentsBuildingOnboardingRecheck
} from "../../../PaymentUtils";

const FLOW_STEP = {
  FORM: 'form',
  CREATING_ACCOUNT: 'creating_account',
  REQUESTING_EMBED_URL: 'requesting_embed_url',
  AWAITING_TOKEN: 'awaiting_token',
  SYNCING: 'syncing',
};

/** VoPay GET user payload may use camelCase phoneNumber (current API). */
function residentPhoneFromVopayPayload(data) {
  if (data == null || typeof data !== 'object') return '';
  const raw = data.phoneNumber ?? data.PhoneNumber;
  return typeof raw === 'string' ? raw : '';
}

const AccountNotSetupModal = () => {
  const { t } = useTranslation();

  const schema = useMemo(
    () =>
      Yup.object().shape({
        phoneNumber: Yup.string()
          .trim()
          .test(
            'phone',
            t('Invalid phone number'),
            (value) => {
              const phoneOnlyNumber = (value ?? '').replace(/\D/g, '');
              if (phoneOnlyNumber.length === 0) return false;
              if (phoneOnlyNumber.length !== 10) return false;
              return /^[2-9][0-9]{2}[2-9][0-9]{6}$/.test(phoneOnlyNumber);
            }
          ),
      }),
    [t]
  );

  const dispatch = useDispatch();
  const modal = useSelector((state) => state.headerModal.modal);
  const modalData = useSelector((state) => state.headerModal.data);
  const addPayment = useSelector((state) => state.addPayment);
  const globalSelectedBuilding = useSelector((state) => state.building.globalSelectedBuilding);
  const paymentMethodSetupOnly = Boolean(modalData?.paymentMethodSetupOnly);
  const isBuildingAccount = modalData?.accountType === 'building';
  const popupRef = useRef(null);
  const pollRef = useRef(null);
  const tokenProcessedRef = useRef(false);
  const prevModalRef = useRef(null);
  const [flowStep, setFlowStep] = useState(FLOW_STEP.FORM);

  const form = useForm({
    resolver: yupResolver(schema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      phoneNumber: '',
    },
  });

  const phoneNumberWatch = form.watch('phoneNumber');

  const canSubmit = useMemo(() => {
    try {
      return schema.isValidSync({
        phoneNumber: phoneNumberWatch,
      });
    } catch {
      return false;
    }
  }, [schema, phoneNumberWatch]);

  const selectedBuildingID = isBuildingAccount
    ? (modalData?.buildingId ?? globalSelectedBuilding?.id)
    : addPayment?.selectedBuilding?.id;
  const selectedUserID = addPayment?.userId?.value;
  const vopayAccountType = isBuildingAccount ? 'building' : 'user';
  const vopayAccountID = isBuildingAccount ? selectedBuildingID : selectedUserID;

  const currentRedirectURL = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}${window.location.pathname}`;
  }, []);

  const openErrorToast = (message) => {
    dispatch(
      setToast({
        toastShow: true,
        toastMessage: message,
        modal: 'errorToast'
      })
    );
  };

  const shouldContinueAfterCreateAccountError = (err) => {
    const message = (err?.data?.message || err?.message || '').toLowerCase();
    return message.includes('already') || message.includes('exists');
  };

  const cleanupPopup = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
  }, []);

  const syncToken = useCallback(
    (token) => {
      if (!token || tokenProcessedRef.current) return;
      tokenProcessedRef.current = true;
      setFlowStep(FLOW_STEP.SYNCING);

      dispatch(
        syncPaymentMethod({
          body: {
            buildingID: selectedBuildingID,
            AccountIDToSync: vopayAccountID,
            accountType: vopayAccountType,
            Token: token
          },
          success: () => {
            cleanupPopup();
            closeActiveModal();
            if (isBuildingAccount) {
              dispatchPaymentsBuildingOnboardingRecheck();
            } else if (paymentMethodSetupOnly) {
              openModal('SelectPaymentTypeModal');
            } else {
              openModal('AccountSuccessSetupModal');
            }
          },
          fail: (err) => {
            tokenProcessedRef.current = false;
            setFlowStep(FLOW_STEP.AWAITING_TOKEN);
            openErrorToast(
              t('Could not sync payment method to Vopay account: {{error}}', {
                error: err?.data?.message || t('Unknown error')
              })
            );
          }
        })
      );
    },
    [selectedBuildingID, vopayAccountID, vopayAccountType, isBuildingAccount, paymentMethodSetupOnly, dispatch, t, cleanupPopup]
  );

  const startPolling = useCallback(
    (popup) => {
      if (pollRef.current) clearInterval(pollRef.current);

      pollRef.current = setInterval(() => {
        if (!popup || popup.closed) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (!tokenProcessedRef.current) {
            setFlowStep(FLOW_STEP.FORM);
          }
          return;
        }

        try {
          const href = popup.location?.href;
          const token = extractVopayTokenFromHref(href);
          if (token) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            popup.close();
            syncToken(token);
          }
        } catch {
          // Cross-origin (VoPay domain) — keep polling
        }
      }, 500);
    },
    [syncToken]
  );

  const requestEmbedURL = useCallback(
    (popup) => {
      if (!selectedBuildingID || vopayAccountID == null) {
        if (popup && !popup.closed) popup.close();
        openErrorToast(
          isBuildingAccount
            ? t('Please select a building before setting up the payment account.')
            : t('Please select building and resident before setting up payment account.')
        );
        setFlowStep(FLOW_STEP.FORM);
        return;
      }

      setFlowStep(FLOW_STEP.REQUESTING_EMBED_URL);
      dispatch(
        addPaymentMethod({
          body: {
            BuildingID: selectedBuildingID,
            AccountIDToAdd: vopayAccountID,
            accountType: vopayAccountType,
            paymentMethodType: PAYMENT_METHOD_TYPE_BANK,
            redirectURL: currentRedirectURL
          },
          success: (res) => {
            const url = res?.data?.EmbedURL;
            if (!url) {
              if (popup && !popup.closed) popup.close();
              setFlowStep(FLOW_STEP.FORM);
              openErrorToast(t('Could not initialize Vopay payment setup.'));
              return;
            }
            if (popup && !popup.closed) {
              popup.location.href = url;
            }
            setFlowStep(FLOW_STEP.AWAITING_TOKEN);
            startPolling(popup);
          },
          fail: (err) => {
            if (popup && !popup.closed) popup.close();
            setFlowStep(FLOW_STEP.FORM);
            openErrorToast(t('Could not initialize Vopay payment setup: {{error}}', {
              error: err?.data?.message || t('Unknown error')
            }));
          }
        })
      );
    },
    [selectedBuildingID, vopayAccountID, vopayAccountType, isBuildingAccount, currentRedirectURL, dispatch, t, startPolling]
  );

  const openVopayPopup = () => {
    const popup = window.open('about:blank', 'vopay_setup', 'width=600,height=700,scrollbars=yes');
    if (!popup) {
      openErrorToast(t('Popup was blocked by the browser. Please allow popups and try again.'));
      return null;
    }
    popupRef.current = popup;
    return popup;
  };

  const handleContinueClick = () => {
    const popup = openVopayPopup();
    if (!popup) return;
    requestEmbedURL(popup);
  };

  const onSubmit = (values) => {
    if (!selectedBuildingID || !selectedUserID) {
      openErrorToast(t('Please select building and resident before setting up payment account.'));
      return;
    }

    const popup = openVopayPopup();
    if (!popup) return;

    setFlowStep(FLOW_STEP.CREATING_ACCOUNT);
    dispatch(
      createResidentVopayAccount({
        body: {
          BuildingID: selectedBuildingID,
          UserID: selectedUserID,
          phoneNumber: values.phoneNumber.trim(),
        },
        success: () => {
          requestEmbedURL(popup);
        },
        fail: (err) => {
          if (shouldContinueAfterCreateAccountError(err)) {
            requestEmbedURL(popup);
            return;
          }
          if (popup && !popup.closed) popup.close();
          setFlowStep(FLOW_STEP.FORM);
          openErrorToast(t('Could not create Vopay account: {{error}}', {
            error: err?.data?.message || t('Unknown error')
          }));
        }
      })
    );
  };

  useEffect(() => {
    const prevModal = prevModalRef.current;

    if (modal !== 'AccountNotSetupModal') {
      form.reset();
      setFlowStep(FLOW_STEP.FORM);
      tokenProcessedRef.current = false;
      cleanupPopup();
      prevModalRef.current = modal;
      return;
    }

    if (paymentMethodSetupOnly) {
      prevModalRef.current = modal;
      return;
    }

    const phone = residentPhoneFromVopayPayload(addPayment.userVopayAccountData);
    const isOpening = prevModal !== 'AccountNotSetupModal';

    if (isOpening) {
      form.reset({
        phoneNumber: phone,
      });
    } else if (phone !== '') {
      const current = form.getValues('phoneNumber');
      if (!current) {
        form.setValue('phoneNumber', phone, { shouldDirty: false });
      }
    }

    prevModalRef.current = modal;
  }, [modal, paymentMethodSetupOnly, addPayment.userVopayAccountData, form, cleanupPopup]);

  useEffect(() => {
    return cleanupPopup;
  }, [cleanupPopup]);

  const isLoading =
    flowStep === FLOW_STEP.AWAITING_TOKEN ||
    flowStep === FLOW_STEP.SYNCING ||
    flowStep === FLOW_STEP.CREATING_ACCOUNT ||
    flowStep === FLOW_STEP.REQUESTING_EMBED_URL;

  const renderBodyContent = () => {
    if (flowStep === FLOW_STEP.CREATING_ACCOUNT || flowStep === FLOW_STEP.REQUESTING_EMBED_URL || flowStep === FLOW_STEP.SYNCING) {
      const loadingText = flowStep === FLOW_STEP.CREATING_ACCOUNT
        ? t('Creating Vopay account...')
        : flowStep === FLOW_STEP.REQUESTING_EMBED_URL
        ? t('Preparing secure payment setup...')
        : t('Syncing payment method...');

      return (
        <Flex direction="column" alignItems="center" gap="1rem">
          <LoadingSpinner size={32} color={Palette.BLACK} />
          <DescriptionText>{loadingText}</DescriptionText>
        </Flex>
      );
    }

    if (flowStep === FLOW_STEP.AWAITING_TOKEN) {
      return (
        <Flex direction="column" alignItems="center" gap="1rem">
          <LoadingSpinner size={32} color={Palette.BLACK} />
          <DescriptionText>
            {t('Complete bank setup in the new window. We will continue automatically once finished.')}
          </DescriptionText>
        </Flex>
      );
    }

    if (paymentMethodSetupOnly) {
      return (
        <DescriptionText style={{ textAlign: 'center' }}>
          {isBuildingAccount
            ? t('Would you like to set up a bank account as the default payment method for this building?')
            : t('Would you like to set up bank account as payment method for this user?')}
        </DescriptionText>
      );
    }

    return (
      <Flex direction="column" alignItems="center" gap="1rem" style={{ width: '100%' }}>
        <DescriptionText>
          {t('This user has not configured a payment account. Would you like to set one up on their behalf?')}
        </DescriptionText>
        <FormField
          control={form.control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem alt style={{ width: '100%' }}>
              <FormLabel v3>{t('Resident\'s phone number')}</FormLabel>
              <FormControl>
                <SettingsModalInput
                  type="tel"
                  inputMode="tel"
                  placeholder={t('Phone number')}
                  autoComplete="tel"
                  {...field}
                  value={field.value ?? ''}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </Flex>
    );
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <PaymentModal
          show={modal === 'AccountNotSetupModal'}
          onClose={closeActiveModal}
          closeOnBackdrop={false}
          width={'460px'}
          padding={"2.25rem 1.5rem 3rem 1.5rem"}
        >
          <StyledModalBody>
            <Flex direction="column" alignItems="center" gap="1.5rem">
              {!isLoading && (
                <IconBackground background={Palette.RUSTY_ORANGE}>
                  <IoWarningOutline
                    size={32}
                    color={Palette.WHITE}
                    style={{ position: 'absolute', right: '12px', top: '11px' }}
                  />
                </IconBackground>
              )}
              <Flex direction="column" alignItems="center" gap="1rem" style={{ width: '100%' }}>
                {!isLoading && (
                  <TitleText lg>
                    {paymentMethodSetupOnly ? t('Payment method not set up') : t('Account not set up')}
                  </TitleText>
                )}
                {renderBodyContent()}
              </Flex>
              <ModalButtonsContainer gapY="8px" column>
                {flowStep === FLOW_STEP.FORM && paymentMethodSetupOnly && (
                  <ButtonBodyVariant type="button" onClick={handleContinueClick} cssOverride={"width: 100%;"}>
                    {t('Continue')}
                  </ButtonBodyVariant>
                )}
                {flowStep === FLOW_STEP.FORM && !paymentMethodSetupOnly && (
                  <ButtonBodyVariant type="submit" disabled={!canSubmit} cssOverride={"width: 100%;"}>
                    {t('Create Account')}
                  </ButtonBodyVariant>
                )}
                {flowStep === FLOW_STEP.AWAITING_TOKEN && (
                  <ButtonBodyVariant
                    variant="secondary"
                    type="button"
                    onClick={() => {
                      cleanupPopup();
                      setFlowStep(FLOW_STEP.FORM);
                    }}
                    cssOverride={"width: 100%;"}
                  >
                    {t('Cancel')}
                  </ButtonBodyVariant>
                )}
                {flowStep === FLOW_STEP.FORM && (
                  <ButtonBodyVariant variant="secondary" type="button" onClick={closeActiveModal} cssOverride={"width: 100%;"}>
                    {paymentMethodSetupOnly ? t('Exit') : t('Cancel')}
                  </ButtonBodyVariant>
                )}
              </ModalButtonsContainer>
            </Flex>
          </StyledModalBody>
        </PaymentModal>
      </form>
    </Form>
  );
};

export default AccountNotSetupModal;
